import type { AccommodationQuickView, AccommodationQuickViewInclusion } from 'voyager';

import {
  getPackageInclusions,
  inclusionLabelKeys,
  isSpaExperience,
} from '@features/packages/utils/getPackageInclusions';
import { TranslationsFunc } from '@lang';
import { Rate } from '../types';

const INCLUSION_SEPARATOR = ' — ';

const toInclusion = (
  id: string,
  groupLabel: string,
  detail?: string | null
): AccommodationQuickViewInclusion => ({
  id,
  label: detail ? `${groupLabel}${INCLUSION_SEPARATOR}${detail}` : groupLabel,
});

const dedupeByLabel = (inclusions: AccommodationQuickViewInclusion[]) => {
  const seen = new Set<string>();
  return inclusions.filter(({ label }) => {
    if (seen.has(label)) return false;
    seen.add(label);
    return true;
  });
};

const getPackageElements = (rate: Rate) => {
  const ratePackage = rate.package;
  return {
    restaurants:
      ratePackage && Array.isArray(ratePackage.restaurants) ? ratePackage.restaurants : [],
    experiences:
      ratePackage && Array.isArray(ratePackage.experiences) ? ratePackage.experiences : [],
  };
};

export const getRateInclusions = (
  rate: Rate,
  t: TranslationsFunc
): AccommodationQuickViewInclusion[] => {
  const categories = getPackageInclusions(rate.package);
  if (categories.length === 0) return [];

  const { restaurants, experiences } = getPackageElements(rate);

  const spa = categories.includes('spa')
    ? experiences.filter(isSpaExperience).map((experience, index) => {
        const options = Array.isArray(experience.options) ? experience.options : [];
        const onlyOption = options.length === 1 ? options[0].name : undefined;
        return toInclusion(`spa-${experience.id ?? index}`, t(inclusionLabelKeys.spa), onlyOption);
      })
    : [];

  const dining = categories.includes('dining')
    ? restaurants.map((restaurant, index) =>
        toInclusion(
          `dining-${restaurant.id ?? restaurant.restaurantId ?? index}`,
          t(inclusionLabelKeys.dining),
          restaurant.name
        )
      )
    : [];

  return dedupeByLabel([{ id: 'room', label: t(inclusionLabelKeys.room) }, ...spa, ...dining]);
};

/**
 * A rate only earns a quick view when its package bundles something beyond the room —
 * a single-element rate has nothing extra to preview.
 */
export const getRateQuickView = (
  rate: Rate,
  t: TranslationsFunc
): AccommodationQuickView | undefined => {
  const inclusions = getRateInclusions(rate, t);
  if (inclusions.length === 0) return undefined;

  const images = (Array.isArray(rate.images) ? rate.images : [])
    .filter(Boolean)
    .map((src, index) => ({ src, alt: `${rate.title} ${index + 1}` }));

  const payablePrice = rate.discount?.discountedPrice ?? rate.price;
  const pricePerStay =
    payablePrice && !rate.unAvailable
      ? { amount: payablePrice.amount, currencyCode: payablePrice.currencyCode }
      : undefined;

  return {
    images: images.length > 0 ? images : undefined,
    description: rate.description || undefined,
    inclusions,
    pricePerStay,
  };
};
